// -----------------------------------------------------------------------------
// engine/render-plan.ts — compile the document into "what to draw at time T".
//
// A pure function: given the current EditorState and a time, it walks the tracks,
// finds the clip active on each, and returns a flat list of layers (media frame
// references + text overlays) in draw order. The compositor consumes this without
// knowing anything about tracks, trims or selection.
//
// Draw order = track array order, so LATER tracks composite on top. The default
// project lists the video track first and the text track last, so text overlays
// land on top of video. (CLAUDE.md §4: engine compiles state → per-frame plan.)
// -----------------------------------------------------------------------------
import { type ID, clipEnd } from '../core/model';
import type { EditorState } from '../core/store';

export interface MediaLayer {
  kind: 'media';
  clipId: ID;
  assetId: ID;
  sourceTimeSec: number; // time within the source media to sample
  opacity: number;
}

export interface TextLayer {
  kind: 'text';
  clipId: ID;
  text: string;
  fontSize: number; // px at project resolution
  color: string;
  fontFamily: string;
  xFrac: number;
  yFrac: number;
  opacity: number;
}

export type RenderLayer = MediaLayer | TextLayer;

export interface RenderPlan {
  timeSec: number;
  layers: RenderLayer[]; // bottom-to-top
}

export function compileRenderPlan(state: EditorState, timeSec: number): RenderPlan {
  const layers: RenderLayer[] = [];

  for (const track of state.project.tracks) {
    // The first clip whose span contains T wins (Tier 0 assumes no overlap).
    for (const clip of track.clips) {
      if (timeSec < clip.startSec || timeSec >= clipEnd(clip)) continue;

      if (clip.kind === 'text') {
        layers.push({
          kind: 'text',
          clipId: clip.id,
          text: clip.text,
          fontSize: clip.fontSize,
          color: clip.color,
          fontFamily: clip.fontFamily,
          xFrac: clip.xFrac,
          yFrac: clip.yFrac,
          opacity: 1,
        });
      } else {
        layers.push({
          kind: 'media',
          clipId: clip.id,
          assetId: clip.assetId,
          sourceTimeSec: clip.inSec + (timeSec - clip.startSec),
          opacity: 1,
        });
      }
      break; // one active clip per track
    }
  }

  return { timeSec, layers };
}
